import React, { useEffect, useState } from "react";
import { QrCode, ChevronRight, ShieldCheck, Wallet, RefreshCw } from "lucide-react";
import { listInjectedWallets, shortAddress, generateQRSvg, buildPaymentURI, DONATION_ADDRESS } from "@/lib/walletService";

/**
 * WalletConnector — wallet picker.
 * Shows all EIP-6963 injected wallets plus a "Scan with mobile wallet"
 * QR-code option (EIP-681 payment URI). Never stores credentials.
 */
export const WalletConnector = ({ amount, onPick, onQR, connectedAccount }) => {
  const [wallets, setWallets] = useState(() => listInjectedWallets());
  const [showQR, setShowQR] = useState(false);
  const [qrSvg, setQrSvg] = useState(null);
  const [qrError, setQrError] = useState(null);

  // Re-scan every 700ms for a short while — wallets may announce late
  useEffect(() => {
    const t = setInterval(() => setWallets(listInjectedWallets()), 700);
    setTimeout(() => clearInterval(t), 3500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!showQR) return;
    let ok = true;
    setQrError(null);
    (async () => {
      try {
        const uri = buildPaymentURI(amount);
        const svg = await generateQRSvg(uri);
        if (ok) setQrSvg(svg);
      } catch (e) {
        if (ok) setQrError(e.message || "No se pudo generar el QR");
      }
    })();
    return () => (ok = false);
  }, [showQR, amount]);

  if (showQR) {
    return (
      <div data-testid="wallet-qr">
        <button
          className="text-[11px] uppercase tracking-widest text-white/60 hover:text-white mb-3"
          onClick={() => setShowQR(false)}
          data-testid="wallet-qr-back"
        >
          ← Volver a wallets
        </button>
        <div
          className="rounded-2xl p-4 flex flex-col items-center"
          style={{ background: "rgba(11,11,18,0.7)", border: "1px solid rgba(138,107,255,0.35)" }}
        >
          {qrError ? (
            <div className="text-red-200 text-sm py-8">{qrError}</div>
          ) : qrSvg ? (
            <div
              className="w-56 h-56 rounded-xl overflow-hidden"
              style={{ background: "#0B0B12", boxShadow: "0 0 22px rgba(138,107,255,0.35)" }}
              dangerouslySetInnerHTML={{ __html: qrSvg }}
            />
          ) : (
            <div className="w-56 h-56 rounded-xl flex items-center justify-center text-white/50 text-xs">Generando…</div>
          )}
          <div className="mt-3 text-center text-[11px] text-white/70 leading-relaxed max-w-xs">
            Abre tu wallet móvil compatible con BSC (Trust, TokenPocket, SafePal, MetaMask móvil, …) y escanea este código.
            Confirmarás la donación de <span className="text-white font-semibold">{amount} BNB</span> a la dirección oficial.
          </div>
          <div className="mt-3 text-[10px] font-mono text-white/50 break-all text-center">
            {DONATION_ADDRESS}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wallet-connector">
      {connectedAccount && (
        <div
          className="mb-3 rounded-xl p-3 flex items-center gap-3"
          style={{ background: "rgba(91,46,255,0.14)", border: "1px solid rgba(138,107,255,0.35)" }}
          data-testid="wallet-connected-badge"
        >
          <ShieldCheck size={16} className="text-[#8A6BFF]" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#8A6BFF]">Wallet conectada</div>
            <div className="text-sm font-mono text-white">{shortAddress(connectedAccount)}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2" data-testid="wallet-list">
        {wallets.length === 0 && (
          <div className="rounded-xl p-3 text-[11px] text-white/60 border border-white/10">
            <Wallet size={14} className="inline mr-2" />
            No detectamos ninguna wallet inyectada. Instala MetaMask, Rabby o Coinbase Wallet como extensión, o usa el QR para wallets móviles.
          </div>
        )}
        {wallets.map((w) => (
          <button
            key={w.uuid}
            data-testid={`wallet-${w.rdns || w.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => onPick(w)}
            className="group flex items-center gap-3 rounded-2xl px-3.5 py-2.5 border border-white/10 hover:border-[#8A6BFF]/60 hover:bg-white/5 transition-colors"
          >
            <span
              className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden"
              style={{ background: "rgba(11,11,18,0.75)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              {w.icon ? (
                <img src={w.icon} alt="wallet logo" className="w-6 h-6" />
              ) : (
                <Wallet size={16} className="text-[#8A6BFF]" />
              )}
            </span>
            <div className="flex-1 text-left min-w-0">
              <div className="text-sm font-semibold text-white truncate">{w.name}</div>
              <div className="text-[10px] text-white/50 truncate">Firma la transacción desde tu wallet</div>
            </div>
            <ChevronRight size={16} className="text-white/40 group-hover:text-white" />
          </button>
        ))}

        <button
          data-testid="wallet-qr-open"
          onClick={() => {
            setShowQR(true);
            onQR?.();
          }}
          className="mt-1 flex items-center gap-3 rounded-2xl px-3.5 py-2.5 border border-dashed border-[#8A6BFF]/40 hover:border-[#8A6BFF] hover:bg-[#5B2EFF]/10 transition-colors"
        >
          <span
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "rgba(91,46,255,0.2)" }}
          >
            <QrCode size={16} className="text-[#8A6BFF]" />
          </span>
          <div className="flex-1 text-left">
            <div className="text-sm font-semibold text-white">Escanear con wallet móvil</div>
            <div className="text-[10px] text-white/60">Trust, TokenPocket, SafePal, MetaMask móvil…</div>
          </div>
          <ChevronRight size={16} className="text-white/40" />
        </button>

        <button
          data-testid="wallet-refresh"
          onClick={() => setWallets(listInjectedWallets())}
          className="mt-1 text-[10px] uppercase tracking-widest text-white/40 hover:text-white/70 inline-flex items-center gap-1 justify-center py-1"
        >
          <RefreshCw size={11} /> Actualizar wallets detectadas
        </button>
      </div>
    </div>
  );
};

export default WalletConnector;
